<template>
  <article class="add-payback">
    <h3 class="add-payback__title">
      {{ spellTransactionType(transaction, user) }}
    </h3>
    <section class="image-and-form">
      <image-upload
        @loadStart="imageUrl = null"
        @loadImage="loadImage"
        @imageError="handleError"
        :value="imageUrl"
        size="43px"
      />
      <form @submit.prevent="submitTransaction" class="add-payback-form">
        <div class="input-group_name">
          <app-input-small
            :default-text="name"
            v-model="$v.name.$model"
            :error="$v.name.$error"
            @input="chooseError"
            :label="$t('CP.purpose')"
            :focus-on-mount="true"
          />
        </div>
        <div class="input-group_payee">
          <app-select-small
            :label="$t('CP.sender')"
            :default="pickName(0)"
            @changeSelect="(input) => payee = input"
            class="transaction-select transaction-select_left"
            :indexes="contactsSelectInputs.indexes"
            :keys="contactsSelectInputs.keys"
            :values="contactsSelectInputs.values"
            :disabled-values="contactsSelectInputs.disabled"
          />
          <app-select-small
            :label="$t('CP.recipient')"
            :default="pickName(1)"
            @changeSelect="(input) => recipient = input"
            class="transaction-select"
            :indexes="contactsSelectInputs.indexes"
            :keys="contactsSelectInputs.keys"
            :values="contactsSelectInputs.values"
            :disabled-values="contactsSelectInputs.disabled"
          />
        </div>
        <div class="input-group_money">
          <div
            v-if="transaction !== 'split purchase'"
            class="transaction-input_money">
            <app-input-small
              v-model="$v.transactionSum.$model"
              :error="$v.transactionSum.$error"
              :default-text="Math.abs(paybackInfo.money).toString()"
              @input="chooseError"
              type="number"
              to-fixed-two
              placeholder="0.00"
              :label="$t('CP.amount')"
            />
          </div>
          <span class="transaction-sum" v-else>
            {{ $t('CP.amount') }}: {{transactionSum | toTwoFixed}}
          </span>
          <app-select-small
            class="select-currency"
            :indexes="currencySelectInputs.indexes"
            :keys="currencySelectInputs.keys"
            :values="currencySelectInputs.values"
            :default="paybackInfo.currency || user.currency || ''"
          />
        </div>
      </form>
    </section>
    <div class="invite-unregistered">
      <b-form-checkbox
        v-if="isInviteAllVisible"
        v-model="inviteAll"
      >
        {{ $t('CP.inviteUnregistered') }}
      </b-form-checkbox>
    </div>
    <span
        v-if="$v.$anyError"
        class="invalid-message">
        {{ errorMessage }}
    </span>
    <span
        v-else-if="imageError"
        class="invalid-message">
        {{ imageErrorMessage }}
    </span>
    <div class="form-buttons">
      <button-app
        class="form-button form-button_cancel"
        @clicked="cancelPaybackSubmit"
        color-scheme="gray"
        type="button">
        {{ $t('CP.cancel') }}
      </button-app>
      <button-app
        class="form-button form-button_save"
        color-scheme="blue"
        @clicked="submitTransaction"
        type="button"
      >
        {{ $t('CP.create') }}
      </button-app>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex';
import { required, minValue } from 'vuelidate/lib/validators';
import {
  ADD_TRANSACTION_REQUEST,
  EVENT_PAYOUTS_REQUEST, FETCH_EVENT_EXPENSES_REQUEST, FETCH_EVENT_SUCCESS,
  // FETCH_EVENT_REQUEST,
  // FETCH_EVENTS_REQUEST,
} from '@/store/events/types';
import { DELETE_FINAL_EVENT_SELECTED_CONTACTS } from '@/store/user/types';
import ImageUpload from '../../components/ImageUpload/ImageUpload';
import AppInputSmall from '../../components/AppInputSmall/AppInputSmall';
import AppSelectSmall from '../../components/AppSelectSmall/AppSelectSmall';
import ButtonApp from '../../components/Button/ButtonApp';
import functionsWithForms from '../../utils/mixins/functionsWithForms';

export default {
  name: 'AddPaybackForm',
  components: {
    ButtonApp,
    AppSelectSmall,
    AppInputSmall,
    ImageUpload,
  },
  extends: functionsWithForms,
  props: {
    operationType: {
      type: String,
      default: '',
    },
    spellTransactionType: {
      type: Function,
      default: () => '',
    },
  },
  validations: {
    name: {
      required,
    },
    transactionSum: {
      required,
      minValue: minValue(0.1),
    },
  },
  data() {
    return {
      name: this.$t('CP.payback'),
      imageUrl: '',
      transactionSum: '',
      payee: '',
      recipient: '',
      inviteAll: false,
      currentCurrency: '',
      imageError: false,
      imageErrorMessage: '',
    };
  },
  mounted() {
    if (this.paybackInfo.type === 'debit') {
      this.payee = this.user.phone;
      this.recipient = this.paybackInfo.user.phone;
    }
    if (this.paybackInfo.type === 'accrual') {
      this.payee = this.paybackInfo.user.phone;
      this.recipient = this.user.phone;
    }
    this.transactionSum = Math.abs(this.paybackInfo.money);
    this.currentCurrency = this.paybackInfo.currency;
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      event: (state) => state.events.event,
      paybackInfo: (state) => state.operations.paybackInfo,
      clonedInfo: (state) => state.operations.cloneOperationInfo,
      currencies: (state) => state.currency.currencies,
      transaction: (state) => state.operations.selectedOperation,
    }),
    isInviteAllVisible() {
      const payee = this.getContactByPhone(this.payee);
      const recipient = this.getContactByPhone(this.recipient);
      // eslint-disable-next-line camelcase
      return !(payee?.is_already_invited && recipient?.is_already_invited);
    },
    currencySelectInputs() {
      const indexes = [];
      const keys = [];
      const values = [];
      this.currencies.forEach((currency) => {
        indexes.push(currency.id);
        keys.push(currency.currency);
        values.push(currency.currency);
      });
      return { indexes, keys, values };
    },
    contactsSelectInputs() {
      const indexes = [];
      const keys = [];
      const values = [];
      const disabled = [];
      this.contactList.forEach((contact) => {
        const recipient = this.getContactByPhone(this.recipient);
        const payee = this.getContactByPhone(this.payee);
        indexes.push(contact.uid);
        keys.push(this.$getUserName(contact));
        values.push(contact.phone);
        if ((payee && payee.uid === contact.uid) || (recipient && recipient.uid === contact.uid)) {
          disabled.push(contact.uid);
        }
      });
      return {
        indexes,
        keys,
        values,
        disabled,
      };
    },
    contactList() {
      const result = [];
      if (this.event.contacts) {
        this.event.contacts.forEach((contact) => {
          if (contact.full_name === this.user.full_name) {
            result.unshift({ ...contact, full_name: this.$t('CP.me') });
          } else {
            result.push(contact);
          }
        });
      }
      return result;
    },
  },
  methods: {
    chooseError() {
      if (!this.$v.transactionSum.minValue) {
        this.errorMessage = `${this.$t('validateText.minAmount')} 0.1!`;
      } else if (this.$v.$anyError) {
        this.errorMessage = `${this.$t('validateText.fillInTheFields')}!`;
      }
    },
    handleError(message) {
      this.imageErrorMessage = message;
      this.imageError = !!message;
    },
    pickName(userNumber) {
      if (userNumber === 0) {
        if (this.paybackInfo.type === 'debit') {
          return this.$t('CP.me');
        }
        if (this.paybackInfo.type === 'accrual') {
          return this.$getUserName(this.paybackInfo.user);
        }
      }
      if (userNumber === 1) {
        if (this.paybackInfo.type === 'accrual') {
          return this.$t('CP.me');
        }
        if (this.paybackInfo.type === 'debit') {
          return this.$getUserName(this.paybackInfo.user);
        }
      }
      return this.$getUserName(this.paybackInfo.user);
    },
    cancelPaybackSubmit() {
      this.cancelSubmit(DELETE_FINAL_EVENT_SELECTED_CONTACTS, 'add-transaction');
    },
    async submitTransaction() {
      this.$v.$touch();
      if (this.$v.$anyError || this.imageError) {
        return;
      }
      const data = {
        details: this.name,
        amount: this.transactionSum,
        currency: this.currentCurrency,
        event_id: this.event.id,
        payee: this.payee,
        invite_all: this.inviteAll,
        operation_type: 'payback',
        participants: [{ phone: this.recipient, amount: this.transactionSum }],
      };
      let imageUrl = '';
      if (this.imageUrl) {
        // eslint-disable-next-line prefer-destructuring
        imageUrl = this.imageUrl.split('base64,')[1];
      }
      data.image_url = imageUrl || null;
      const transactionData = await this.$store.dispatch(ADD_TRANSACTION_REQUEST, data);
      if (transactionData && transactionData.event_data) {
        this.$store.commit(FETCH_EVENT_SUCCESS, transactionData.event_data);
        this.$store.dispatch(FETCH_EVENT_EXPENSES_REQUEST, {
          ...transactionData.event_data,
          utcOffset: this.$moment().utcOffset(),
          currency: transactionData.currency,
          isUpdate: true,
        });
        this.$store.dispatch(EVENT_PAYOUTS_REQUEST, {
          ...transactionData.event_data,
          currency: transactionData.currency,
        });
      }
      // await this.$store.dispatch(FETCH_EVENT_REQUEST, {
      //   id: this.event.id,
      //   currency: data.currency,
      // });
      // this.$store.dispatch(FETCH_EVENTS_REQUEST);
      this.$bvModal.hide('add-transaction');
    },
    getContactByPhone(phone) {
      try {
        const inEventContacts = this.event.contacts.find((contact) => contact.phone === phone);
        const inFriends = this.$store.state.user.userContacts
          .find((contact) => contact.phone === phone);
        if (inEventContacts && inEventContacts.uid === this.user.user) {
          inEventContacts.full_name = this.$t('CP.me');
        }
        if (inFriends && inFriends.uid === this.user.user) {
          inFriends.full_name = this.$t('CP.me');
        }
        return inEventContacts || inFriends;
      } catch (e) {
        return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .add-payback {
    color: var(--text-main-color);
    min-height: 329px;
  }
  .add-payback__title {
    margin: 0 0 26px;
  }
  .image-and-form {
    display: flex;
    flex-direction: row;
  }
  .add-payback-form {
    flex: 1 0 20%;
    display: flex;
    flex-wrap: wrap;
  }
  .input-group_name {
    flex: 1 0 100%;
  }
  .input-group_payee {
    flex: 0 0 100%;
    display: flex;
    align-items: flex-end;
    margin: 0 10px 0 0;
  }
  .input-group_money {
    flex: 1 0 100%;
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
  }
  .transaction-input_money {
    flex: 1;
    margin: 0 8px 0 0;
  }
  .transaction-sum {
    display: flex;
    flex: 1 0;
    align-items: flex-end;
    height: 49px;
    margin: 0 8px 0 0;
    color: var(--text-main-color);
  }
  .select-currency {
    flex: 0 1 52px;
  }
  .invite-unregistered {
    flex: 1 0 100%;
    margin-top: 18px;
  }
  .form-buttons {
    display: flex;
    flex: 1 0 50%;
    justify-content: flex-end;
    margin: 22px 0 0 0;
  }
  .form-button {
    width: 160px;
    height: 38px;
    margin: 0;
  }
  .form-button_cancel {
    margin: 0 8px 0 0;
  }
  .transaction-select {
    flex: 1 0 40%;
  }
  .transaction-select_left {
    margin-right: 24px;
  }
  @media screen and (max-width: $breakpoint-sm) {
    .input-group_payee {
      flex: 1 0 100%;
      flex-wrap: wrap;
    }
    .transaction-select {
      flex: 1 0 100%;
    }
    .image-and-form {
      flex-direction: column;
      align-items: center;
    }
    .form-buttons {
      justify-content: center;
      left: 16px;
      right: 16px;
      bottom: 4svh;
    }
  }
</style>
